/* eslint-disable arrow-parens */
// A button showing the current selection's thumbnail, opening a dialog with
// a thumbnail grid grouped by scope (Employer Logo / Shared / This Group's
// uploads) — click a thumbnail to preview it large as the pending
// selection, then "Use this Image" to confirm. Includes an inline "Upload
// new" shortcut so adding a one-off image doesn't require leaving the card
// builder. Replaces the old plain <Select> of asset_key strings, which gave
// no visual preview of what was actually being picked.
import { useState } from "react";
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Avatar,
	TextField,
	CircularProgress,
	Divider
} from "@material-ui/core";
import { api } from "../../../../utils/api";
import { generateFileURL } from "../../../../utils/commonFunctions/generateFileURL";
import { HealthCardAssetKey, HealthCardAssetKeysResult } from "../../../../@types/healthCardConfig.types";

interface AssetKeyPickerProps {
	assetKeys?: HealthCardAssetKeysResult;
	value: string;
	onChange: (assetKey: string) => void;
	label?: string;
}

// Same PNG-transparency reasoning the old picker's comment had — a plain
// <img>, not <Avatar>, so a transparent logo doesn't pick up Avatar's root
// div's default background color.
const Thumb = ({ url, size = 32 }: { url: string | null; size?: number }) =>
	url ? (
		<img src={url} alt="" style={{ width: size, height: size, objectFit: "contain" }} />
	) : (
		<Avatar variant="square" style={{ width: size, height: size, fontSize: size / 3 }}>
			?
		</Avatar>
	);

const ThumbTile = ({
	asset,
	selected,
	onClick
}: {
	asset: HealthCardAssetKey;
	selected: boolean;
	onClick: () => void;
}) => (
	<div
		onClick={onClick}
		style={{
			cursor: "pointer",
			border: selected ? "2px solid #1976d2" : "1px solid #ddd",
			borderRadius: 4,
			padding: 8,
			width: 96,
			textAlign: "center"
		}}
	>
		<Thumb url={asset.url} size={48} />
		<Typography variant="caption" component="div" style={{ wordBreak: "break-word", marginTop: 4 }}>
			{asset.label || asset.asset_key}
		</Typography>
	</div>
);

const AssetKeyPicker = ({ assetKeys, value, onChange, label }: AssetKeyPickerProps) => {
	const [dialogOpen, setDialogOpen] = useState(false);
	const [pendingKey, setPendingKey] = useState("");
	// Assets uploaded via this dialog's own "Upload new" shortcut this
	// session — merged on top of whatever assetKeys the parent last fetched,
	// so the newly-uploaded image is pickable immediately without the parent
	// needing to know about it or re-fetch.
	const [localAssets, setLocalAssets] = useState<HealthCardAssetKey[]>([]);

	const [uploadOpen, setUploadOpen] = useState(false);
	const [file, setFile] = useState<File | null>(null);
	const [uploadLabel, setUploadLabel] = useState("");
	const [uploadKey, setUploadKey] = useState("");
	const [uploading, setUploading] = useState(false);

	const allAssets = [...(assetKeys?.assets ?? []), ...localAssets];
	const employerLogo = assetKeys?.employer_logo;
	const sharedAssets = allAssets.filter((asset) => asset.scope === "global");
	const groupAssets = allAssets.filter((asset) => asset.scope === "group");

	const findAsset = (assetKey: string): HealthCardAssetKey | undefined =>
		employerLogo && assetKey === employerLogo.asset_key
			? { asset_key: employerLogo.asset_key, url: employerLogo.url ?? "", label: "Employer Logo", scope: "group" }
			: allAssets.find((asset) => asset.asset_key === assetKey);

	const selectedAsset = value ? findAsset(value) : undefined;

	const openDialog = () => {
		setPendingKey(value);
		setUploadOpen(false);
		setDialogOpen(true);
	};
	const closeDialog = () => setDialogOpen(false);

	const confirmSelection = () => {
		onChange(pendingKey);
		setDialogOpen(false);
	};

	const resetUploadForm = () => {
		setFile(null);
		setUploadLabel("");
		setUploadKey("");
	};

	const handleUpload = async () => {
		if (!file || !uploadKey.trim() || !uploadLabel.trim()) return;
		setUploading(true);
		try {
			const formData = new FormData();
			formData.append("attachment", file);
			// group_number omitted deliberately — only the Employer persona
			// reaches this builder, and the backend always forces the
			// uploader's own group_number for that role regardless of what's
			// sent, so there's no ambiguity to resolve client-side here.
			formData.append(
				"health_card_asset",
				JSON.stringify({ asset_key: uploadKey.trim(), label: uploadLabel.trim() })
			);
			const result = await api.healthCardAsset.uploadHealthCardAsset(formData);
			const newAsset: HealthCardAssetKey = {
				asset_key: result.asset_key,
				label: result.label,
				url: generateFileURL(result.attachment),
				scope: "group"
			};
			setLocalAssets((prev) => [...prev, newAsset]);
			setPendingKey(newAsset.asset_key);
			setUploadOpen(false);
			resetUploadForm();
		} finally {
			setUploading(false);
		}
	};

	return (
		<>
			<Button
				variant="outlined"
				onClick={openDialog}
				fullWidth
				style={{ justifyContent: "flex-start", textTransform: "none", padding: "6px 10px" }}
			>
				<Thumb url={selectedAsset?.url || null} size={24} />
				<span style={{ marginLeft: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					{selectedAsset ? selectedAsset.label || selectedAsset.asset_key : label ?? "Choose a logo"}
				</span>
			</Button>

			<Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
				<DialogTitle>Choose an Image</DialogTitle>
				<DialogContent>
					{pendingKey && (
						<div style={{ textAlign: "center", marginBottom: 16 }}>
							<Thumb url={findAsset(pendingKey)?.url || null} size={100} />
						</div>
					)}

					<div
						onClick={() => setPendingKey("")}
						style={{
							cursor: "pointer",
							border: pendingKey === "" ? "2px solid #1976d2" : "1px solid #ddd",
							borderRadius: 4,
							padding: 8,
							marginBottom: 16,
							display: "inline-block"
						}}
					>
						<Typography variant="caption">None</Typography>
					</div>

					<Divider style={{ margin: "12px 0" }} />

					{employerLogo && (
						<>
							<Typography variant="subtitle2" style={{ marginTop: 8 }}>
								Employer Logo
							</Typography>
							<div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
								<ThumbTile
									asset={{
										asset_key: employerLogo.asset_key,
										label: "Employer Logo",
										url: employerLogo.url ?? "",
										scope: "group"
									}}
									selected={pendingKey === employerLogo.asset_key}
									onClick={() => setPendingKey(employerLogo.asset_key)}
								/>
							</div>
							<Divider style={{ margin: "12px 0" }} />
						</>
					)}

					{groupAssets.length > 0 && (
						<>
							<Typography variant="subtitle2" style={{ marginTop: 8 }}>
								This Group&apos;s Uploads
							</Typography>
							<div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
								{groupAssets.map((asset) => (
									<ThumbTile
										key={asset.asset_key}
										asset={asset}
										selected={pendingKey === asset.asset_key}
										onClick={() => setPendingKey(asset.asset_key)}
									/>
								))}
							</div>
							<Divider style={{ margin: "12px 0" }} />
						</>
					)}

					{sharedAssets.length > 0 && (
						<>
							<Typography variant="subtitle2" style={{ marginTop: 8 }}>
								Shared
							</Typography>
							<div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
								{sharedAssets.map((asset) => (
									<ThumbTile
										key={asset.asset_key}
										asset={asset}
										selected={pendingKey === asset.asset_key}
										onClick={() => setPendingKey(asset.asset_key)}
									/>
								))}
							</div>
							<Divider style={{ margin: "12px 0" }} />
						</>
					)}

					{uploadOpen ? (
						<div style={{ marginTop: 16, padding: 12, border: "1px solid #ddd", borderRadius: 4 }}>
							<input
								id="asset-key-picker-file-input"
								type="file"
								accept="image/png, image/jpeg, image/svg+xml"
								onChange={(event) => setFile(event.target.files?.[0] ?? null)}
								style={{ display: "none" }}
							/>
							<label htmlFor="asset-key-picker-file-input">
								<Button variant="outlined" component="span" size="small">
									{file ? file.name : "Choose Image"}
								</Button>
							</label>
							<TextField
								label="Label"
								value={uploadLabel}
								onChange={(event) => setUploadLabel(event.target.value)}
								fullWidth
								variant="outlined"
								margin="dense"
								size="small"
							/>
							<TextField
								label="Asset Key"
								value={uploadKey}
								onChange={(event) => setUploadKey(event.target.value)}
								fullWidth
								variant="outlined"
								margin="dense"
								size="small"
							/>
							<div style={{ marginTop: 8, textAlign: "right" }}>
								<Button size="small" onClick={() => setUploadOpen(false)} disabled={uploading}>
									Cancel
								</Button>
								<Button
									size="small"
									variant="contained"
									color="primary"
									onClick={handleUpload}
									disabled={uploading || !file || !uploadKey.trim() || !uploadLabel.trim()}
									style={{ color: "#fff" }}
								>
									{uploading ? <CircularProgress size={16} style={{ color: "#fff" }} /> : "Upload"}
								</Button>
							</div>
						</div>
					) : (
						<Button
							size="small"
							variant="outlined"
							color="primary"
							onClick={() => setUploadOpen(true)}
							style={{ marginTop: 8 }}
						>
							Upload new
						</Button>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeDialog}>Cancel</Button>
					<Button variant="contained" color="primary" onClick={confirmSelection} style={{ color: "#fff" }}>
						Use this Image
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default AssetKeyPicker;
